import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import type { EdgeId } from '@ipfabric/utils';
import { nullable } from '@ipfabric/utils/fp/nullable';
import { MultiClickDispatcher } from 'components/Graph/GraphApp/helpers/MultiClickDispatcher';
import type { GraphRendererPlugin } from 'components/Graph/GraphApp/plugins/GraphRendererPlugin';
import { isPathLookupGraphMetadata } from 'components/Graph/GraphState';
import { ungroupEdge } from 'components/Graph/commands/edges/ungroupEdge';
import { selectPathInspectorHighlightedEdge } from 'components/Graph/commands/pathLookup/selectPathInspectorHighlightedEdge';
import { canSelectHighlightedPathLookupEdge } from 'components/Graph/helpers/canSelectHighlightedPathLookupEdge';
import { hasOwnProperty as recordHasOwnProperty } from 'fp-ts/lib/Record';
import { flow } from 'fp-ts/lib/function';
import { type GraphEdgeDisplayObject, getEdgeId } from '../GraphEdge';

export const edgeClick: GraphRendererPlugin = (pluginInterface) => {
  const getClickedEdgeId = (edge: GraphEdgeDisplayObject): EdgeId | null => {
    const edgeId = getEdgeId(edge);
    if (!recordHasOwnProperty(edgeId, pluginInterface.getGraphData().edges)) {
      stumble('Clicked edge that is not present in data');
      return null;
    }
    return edgeId;
  };

  const isPathInspectorHighlightedEdge = (edgeId: EdgeId) =>
    isPathLookupGraphMetadata(pluginInterface.getGraphMetadata()) &&
    canSelectHighlightedPathLookupEdge(edgeId, pluginInterface.getPathlookup());
  const handleEdgeClick = (edge: GraphEdgeDisplayObject) => {
    const edgeId = getEdgeId(edge);
    if (isPathInspectorHighlightedEdge(edgeId)) {
      pluginInterface.graphDispatch(selectPathInspectorHighlightedEdge, {
        edgeId,
      });
      edge.cursor = 'default';
    }
  };

  const handleEdgeMouseOver = (
    edge: GraphEdgeDisplayObject,
    edgeId: EdgeId,
  ) => {
    if (isPathInspectorHighlightedEdge(edgeId)) {
      edge.cursor = 'pointer';
    }
  };

  const handleEdgeMouseOut = (edge: GraphEdgeDisplayObject, edgeId: EdgeId) => {
    if (isPathInspectorHighlightedEdge(edgeId)) {
      edge.cursor = 'default';
    }
  };

  const handleEdgeDoubleClick: (id: EdgeId) => void = flow(
    // cannot use propOf(pluginInterface.getGraphData().edges) as it
    // would hold reference to initial graphData and not receive updates.
    // Fixed noUncheckedIndexedAccess NIM-19408
    (edgeId) => pluginInterface.getGraphData().edges[edgeId]!,
    (edgeData) => {
      pluginInterface.graphDispatch(ungroupEdge, { edgeData });
    },
  );

  const clickDispatcher = new MultiClickDispatcher<GraphEdgeDisplayObject>(
    pluginInterface,
    {
      onClick: handleEdgeClick,
      onDoubleClick: flow(
        getClickedEdgeId,
        nullable.chain(handleEdgeDoubleClick),
      ),
    },
  );

  pluginInterface.events.addListener(
    'edgeMouseDown',
    clickDispatcher.handleMouseDown,
  );

  pluginInterface.events.addListener(
    'interruptClick',
    clickDispatcher.interruptClick,
  );

  pluginInterface.events.addListener('edgeAdded', ({ edge, id }) => {
    edge.on('mouseover', () => handleEdgeMouseOver(edge, id));
    edge.on('mouseout', () => handleEdgeMouseOut(edge, id));
  });
};
